export const PERMISSIONS = {
  FLOORPLAN_FLOORS: "FLOORPLAN_FLOORS",
  FLOORPLAN_SECTIONS: "FLOORPLAN_SECTIONS",
  FLOORPLAN_TABLES: "FLOORPLAN_TABLES",
  ADMIN_USERS: "ADMIN_USERS",
  ADMIN_ACCESS_LEVELS: "ADMIN_ACCESS_LEVELS",
  ADMIN_AUDIT: "ADMIN_AUDIT",
  CONFIG_PRINTERS: "CONFIG_PRINTERS",
  CONFIG_DEVICES: "CONFIG_DEVICES",
  CONFIG_POS: "CONFIG_POS",
  CONFIG_KDS: "CONFIG_KDS",
  CONFIG_KIOSK: "CONFIG_KIOSK",
  CONFIG_BRANDING: "CONFIG_BRANDING",
  CONFIG_PAYMENTS: "CONFIG_PAYMENTS",
  REPORTS_SALES: "REPORTS_SALES",
  REPORTS_STAFF: "REPORTS_STAFF",
  REPORTS_FINANCIAL: "REPORTS_FINANCIAL",
  POS: "POS",
  ORDERS_LIVE: "ORDERS_LIVE",
  ORDERS_HISTORY: "ORDERS_HISTORY",
  FLOOR: "FLOOR",
  RESERVATIONS: "RESERVATIONS",
  CASH_DRAWER: "CASH_DRAWER",
  DAY_CLOSE: "DAY_CLOSE",
  WORKFORCE_STAFF: "WORKFORCE_STAFF",
  WORKFORCE_SCHEDULES: "WORKFORCE_SCHEDULES",
  WORKFORCE_PUNCHES: "WORKFORCE_PUNCHES",
  CATALOG_ITEMS: "CATALOG_ITEMS",
  CATALOG_CATEGORIES: "CATALOG_CATEGORIES",
  CATALOG_MODIFIERS: "CATALOG_MODIFIERS",
  PRICING_PROMOTIONS: "PRICING_PROMOTIONS",
  PRICING_TAX: "PRICING_TAX",
  MENU_STATION_ASSIGNMENT: "MENU_STATION_ASSIGNMENT",
  FULFILLMENT_PENDING_PAYMENTS: "FULFILLMENT_PENDING_PAYMENTS",
  DEVICES_HEALTH: "DEVICES_HEALTH",
  DEVICES_LOCAL_DEVICES: "DEVICES_LOCAL_DEVICES",
  DEVICES_KIOSKS: "DEVICES_KIOSKS",
  SETTINGS_CONFIGURE_STORE: "SETTINGS_CONFIGURE_STORE",
  SETTINGS_TAX_RATES: "SETTINGS_TAX_RATES",
  SETTINGS_PRINTERS: "SETTINGS_PRINTERS",
  SETTINGS_FULFILLMENT: "SETTINGS_FULFILLMENT",
  SETTINGS_FAQ: "SETTINGS_FAQ",
};

const PERMISSION_ALIASES = {
  CONFIG_POS: [PERMISSIONS.POS],
  CONFIG_DEVICES: [
    "DEVICES_HEALTH",
    "DEVICES_LOCAL_DEVICES",
    "DEVICES_TERMINALS",
  ],
  CONFIG_KIOSK: ["DEVICES_KIOSKS"],
  CONFIG_KDS: ["SETTINGS_FULFILLMENT"],
  CONFIG_PAYMENTS: [
    "SETTINGS_CONFIGURE_STORE",
    "SETTINGS_FULFILLMENT",
    "SETTINGS_FAQ",
  ],
  CONFIG_PRINTERS: ["SETTINGS_PRINTERS"],
  FLOOR: [
    "FLOORPLAN_FLOORS",
    "FLOORPLAN_SECTIONS",
    "FLOORPLAN_TABLES",
  ],
  ADMIN_USERS: ["USERS"],
  ADMIN_ACCESS_LEVELS: ["USERS_ACCESS_LEVELS"],
  CATALOG_ITEMS: ["MENU_ITEMS"],
  CATALOG_CATEGORIES: ["MENU_CATEGORIES"],
  CATALOG_MODIFIERS: ["MENU_STATIONS", "MENU_STATION_ASSIGNMENT"],
  REPORTS_SALES: ["ORDERS"],
  REPORTS_FINANCIAL: ["ORDERS", "CASHUP"],
  CASH_DRAWER: ["CASHUP_TILLS", "CASHUP_TILL_SESSIONS", "CASHUP"],
  DAY_CLOSE: ["CASHUP_TILL_SESSIONS", "CASHUP"],
  DEVICE_HEALTH: ["DEVICES_HEALTH"],
  DEVICES_HEALTH: ["DEVICES_HEALTH"],
  DEVICES_LOCAL_DEVICES: ["DEVICES_LOCAL_DEVICES"],
  DEVICES_KIOSKS: ["DEVICES_KIOSKS"],
  MENU_STATION_ASSIGNMENT: ["MENU_STATION_ASSIGNMENT"],
  SETTINGS_PRINTERS: ["SETTINGS_PRINTERS"],
  SETTINGS_TAX_RATES: ["SETTINGS_TAX_RATES"],
  SETTINGS_CONFIGURE_STORE: ["SETTINGS_CONFIGURE_STORE"],
  SETTINGS_FULFILLMENT: ["SETTINGS_FULFILLMENT"],
  SETTINGS_FAQ: ["SETTINGS_FAQ"],
};

export const parsePermissionList = (permissions) => {
  if (!permissions) return [];
  if (Array.isArray(permissions)) return permissions.filter(Boolean);
  if (typeof permissions === "string") {
    try {
      const parsed = JSON.parse(permissions);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      // ignore parse error
    }
    return permissions.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const toPermissionList = parsePermissionList;

const getPermissionSet = (permissions) => new Set(parsePermissionList(permissions));

const expandPermissionKey = (permission) => {
  const normalized = String(permission || "").trim();
  if (!normalized) return [];
  return PERMISSION_ALIASES[normalized] || [normalized];
};

export const normalizePermissions = (permissions) => {
  const normalized = [];
  const seen = new Set();

  toPermissionList(permissions).forEach((permission) => {
    expandPermissionKey(permission).forEach((expandedPermission) => {
      if (seen.has(expandedPermission)) return;
      seen.add(expandedPermission);
      normalized.push(expandedPermission);
    });
  });

  return normalized;
};

export const getUserPermissions = (user) => normalizePermissions(user?.permissions);

export const getUserExactPermissions = (user) =>
  normalizePermissions(user?.permissionsRaw || user?.permissions);

const getRequiredPermissions = (screenKeys) =>
  toPermissionList(screenKeys).flatMap((screenKey) => expandPermissionKey(screenKey));

export const hasScreenAccess = (user, screenKey) => {
  if (!screenKey) return true;
  const userPermissions = new Set(getUserPermissions(user));
  return getRequiredPermissions(screenKey).some((permission) =>
    userPermissions.has(permission),
  );
};

export const hasPermission = hasScreenAccess;

export const hasAnyScreenAccess = (user, screenKeys = []) => {
  if (!Array.isArray(screenKeys) || screenKeys.length === 0) return true;
  const userPermissions = new Set(getUserPermissions(user));
  return getRequiredPermissions(screenKeys).some((permission) =>
    userPermissions.has(permission),
  );
};

const EXACT_ROUTE_PERMISSION_RULES = [
  {
    path: "/pos",
    permissions: [PERMISSIONS.POS, "POS_MODE_ADMIN"],
    prefixMatch: true,
  },
  {
    path: "/floorplan/floors",
    permissions: ["FLOORPLAN_FLOORS"],
  },
  {
    path: "/floorplan/sections",
    permissions: ["FLOORPLAN_SECTIONS"],
  },
  {
    path: "/floorplan/tables",
    permissions: ["FLOORPLAN_TABLES"],
  },
  { path: "/users", permissions: ["USERS"], prefixMatch: true },
  {
    path: "/users/access-levels",
    permissions: ["USERS_ACCESS_LEVELS"],
    prefixMatch: true,
  },
  {
    path: "/devices/health",
    permissions: ["DEVICES_HEALTH"],
  },
  {
    path: "/devices/local-devices",
    permissions: ["DEVICES_LOCAL_DEVICES"],
  },
  {
    path: "/devices/kiosks",
    permissions: ["DEVICES_KIOSKS"],
  },
  {
    path: "/devices/terminals",
    permissions: ["DEVICES_TERMINALS"],
  },
  {
    path: "/settings/configure-store",
    permissions: ["SETTINGS_CONFIGURE_STORE"],
  },
  {
    path: "/settings/tax-rates",
    permissions: ["SETTINGS_TAX_RATES"],
  },
  {
    path: "/settings/printers",
    permissions: ["SETTINGS_PRINTERS"],
    prefixMatch: true,
  },
  {
    path: "/settings/fulfillment",
    permissions: ["SETTINGS_FULFILLMENT"],
  },
  {
    path: "/settings/faq",
    permissions: ["SETTINGS_FAQ"],
  },
  {
    path: "/menu/categories",
    permissions: ["MENU_CATEGORIES"],
  },
  {
    path: "/menu/items",
    permissions: ["MENU_ITEMS"],
    prefixMatch: true,
  },
  {
    path: "/menu/stations",
    permissions: ["MENU_STATIONS"],
  },
  {
    path: "/menu/station-assignment",
    permissions: ["MENU_STATION_ASSIGNMENT"],
  },
  {
    path: "/reporting/orders",
    permissions: ["ORDERS"],
    prefixMatch: true,
  },
  {
    path: "/reporting/items",
    permissions: ["ORDERS"],
    prefixMatch: true,
  },
  {
    path: "/reporting/order-lifecycle",
    permissions: ["ORDERS"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/tills",
    permissions: ["CASHUP_TILLS", "CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/till-sessions",
    permissions: ["CASHUP_TILL_SESSIONS", "CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/cash-movements",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/reconciliation",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/day-close",
    permissions: ["CASHUP"],
  },
  {
    path: "/pos/cash-up",
    permissions: ["CASHUP"],
  },
  {
    path: "/pos/cash-movements",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/pos/change-payment-method",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/pos/reservations",
    permissions: ["RESERVATIONS"],
    prefixMatch: true,
  },
  {
    path: "/fulfillment/pending-payments",
    permissions: ["FULFILLMENT_PENDING_PAYMENTS"],
  },
];

const getExactRequiredPermissions = (screenKeys) =>
  toPermissionList(screenKeys);

export const getExactRequiredScreenPermission = (pathname) => {
  if (typeof pathname !== "string" || !pathname.trim()) return null;
  const normalizedPath = pathname.trim();
  const rule = EXACT_ROUTE_PERMISSION_RULES.find((candidate) => {
    if (candidate.path === normalizedPath) return true;
    if (!candidate.prefixMatch) return false;
    return normalizedPath.startsWith(`${candidate.path}/`);
  });
  return rule?.permissions || null;
};

export const hasExactScreenAccess = (user, screenKey) => {
  if (!screenKey) return true;
  const userPermissions = getPermissionSet(getUserExactPermissions(user));
  return getExactRequiredPermissions(screenKey).some((permission) =>
    userPermissions.has(permission),
  );
};

export const hasExactAnyScreenAccess = (user, screenKeys = []) => {
  if (!Array.isArray(screenKeys) || screenKeys.length === 0) return true;
  const userPermissions = getPermissionSet(getUserExactPermissions(user));
  return getExactRequiredPermissions(screenKeys).some((permission) =>
    userPermissions.has(permission),
  );
};

export const hasExactRoutePermission = (user, pathname) => {
  const requiredPermissions = getExactRequiredScreenPermission(pathname);
  if (!requiredPermissions) return true;
  return hasExactAnyScreenAccess(user, requiredPermissions);
};

const ROUTE_PERMISSION_RULES = [
  {
    path: "/pos",
    permissions: [PERMISSIONS.POS, "POS_MODE_ADMIN"],
    prefixMatch: true,
  },
  {
    path: "/floorplan/floors",
    permissions: ["FLOORPLAN_FLOORS"],
  },
  {
    path: "/floorplan/sections",
    permissions: ["FLOORPLAN_SECTIONS"],
  },
  {
    path: "/floorplan/tables",
    permissions: ["FLOORPLAN_TABLES"],
  },
  { path: "/users", permissions: ["USERS"], prefixMatch: true },
  {
    path: "/users/access-levels",
    permissions: ["USERS_ACCESS_LEVELS"],
    prefixMatch: true,
  },
  {
    path: "/devices/health",
    permissions: ["DEVICES_HEALTH"],
  },
  {
    path: "/devices/local-devices",
    permissions: ["DEVICES_LOCAL_DEVICES"],
  },
  {
    path: "/devices/kiosks",
    permissions: ["DEVICES_KIOSKS"],
  },
  {
    path: "/devices/terminals",
    permissions: ["DEVICES_TERMINALS"],
  },
  {
    path: "/settings/configure-store",
    permissions: ["SETTINGS_CONFIGURE_STORE"],
  },
  {
    path: "/settings/tax-rates",
    permissions: ["SETTINGS_TAX_RATES"],
  },
  {
    path: "/settings/printers",
    permissions: ["SETTINGS_PRINTERS"],
    prefixMatch: true,
  },
  {
    path: "/settings/fulfillment",
    permissions: ["SETTINGS_FULFILLMENT"],
  },
  {
    path: "/settings/faq",
    permissions: ["SETTINGS_FAQ"],
  },
  {
    path: "/menu/categories",
    permissions: ["MENU_CATEGORIES"],
  },
  {
    path: "/menu/items",
    permissions: ["MENU_ITEMS"],
    prefixMatch: true,
  },
  {
    path: "/menu/stations",
    permissions: ["MENU_STATIONS"],
  },
  {
    path: "/menu/station-assignment",
    permissions: ["MENU_STATION_ASSIGNMENT"],
  },
  {
    path: "/reporting/orders",
    permissions: ["ORDERS"],
    prefixMatch: true,
  },
  {
    path: "/reporting/items",
    permissions: ["ORDERS"],
    prefixMatch: true,
  },
  {
    path: "/reporting/order-lifecycle",
    permissions: ["ORDERS"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/tills",
    permissions: ["CASHUP_TILLS", "CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/till-sessions",
    permissions: ["CASHUP_TILL_SESSIONS", "CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/cash-movements",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/reconciliation",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/cash-management/day-close",
    permissions: ["CASHUP"],
  },
  {
    path: "/pos/cash-up",
    permissions: ["CASHUP"],
  },
  {
    path: "/pos/cash-movements",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/pos/change-payment-method",
    permissions: ["CASHUP"],
    prefixMatch: true,
  },
  {
    path: "/pos/reservations",
    permissions: ["RESERVATIONS"],
    prefixMatch: true,
  },
  {
    path: "/fulfillment/pending-payments",
    permissions: ["FULFILLMENT_PENDING_PAYMENTS"],
  },
];

export const getRequiredScreenPermission = (pathname) => {
  if (typeof pathname !== "string" || !pathname.trim()) return null;
  const normalizedPath = pathname.trim();
  const rule = ROUTE_PERMISSION_RULES.find((candidate) => {
    if (candidate.path === normalizedPath) return true;
    if (!candidate.prefixMatch) return false;
    return normalizedPath.startsWith(`${candidate.path}/`);
  });
  return rule?.permissions || null;
};

export const hasRoutePermission = (user, pathname) => {
  const requiredPermissions = getRequiredScreenPermission(pathname);
  if (!requiredPermissions) return true;
  return hasAnyScreenAccess(user, requiredPermissions);
};
